import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { Building2, UserPlus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useOrgPath } from '@/lib/pathFor'

export interface PromotionContact {
  id?: string | null
  name?: string | null
  email: string
  company?: string | null
}

export interface ClientOption {
  id: string
  name: string
}

export interface PromotionShipmentOption {
  id: string
  origin_port: string | null
  destination_port: string | null
  client_name?: string | null
  unassigned_bookings_count?: number | null
}

interface ContactPromotionModalProps {
  open: boolean
  onClose: () => void
  contact: PromotionContact | null
  clients: ClientOption[]
  emailThreadId?: string | null
  shipments?: PromotionShipmentOption[]
}

function defaultClientName(contact: PromotionContact | null) {
  if (!contact) return ''
  if (contact.company) return contact.company
  if (contact.name) return contact.name

  const domain = contact.email.split('@')[1]
  if (!domain) return ''

  return domain.split('.')[0].replace(/[-_]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

export default function ContactPromotionModal({ open, onClose, contact, clients, emailThreadId, shipments = [] }: ContactPromotionModalProps) {
  const pathFor = useOrgPath()
  const clientlessShipments = shipments.filter((shipment) => !shipment.client_name)
  const [mode, setMode] = useState<'create' | 'existing'>('create')
  const [clientName, setClientName] = useState(defaultClientName(contact))
  const [selectedClientId, setSelectedClientId] = useState('')
  const [selectedShipmentId, setSelectedShipmentId] = useState(clientlessShipments.length === 1 ? clientlessShipments[0].id : '')
  const [saving, setSaving] = useState(false)

  const canSubmit = useMemo(() => {
    if (!contact?.email) return false
    if (mode === 'create') return clientName.trim().length > 0
    return selectedClientId.length > 0
  }, [clientName, contact?.email, mode, selectedClientId])

  function submit() {
    if (!contact || !canSubmit) return

    setSaving(true)
    router.post(pathFor('/contact_promotions'), {
      mode,
      contact_id: contact.id || null,
      sender_email: contact.email,
      contact_name: contact.name || '',
      email_thread_id: emailThreadId || null,
      shipment_id: selectedShipmentId || null,
      client_id: mode === 'existing' ? selectedClientId : null,
      client: mode === 'create' ? { name: clientName.trim(), status: 'active' } : {},
    }, {
      preserveScroll: true,
      onSuccess: () => onClose(),
      onError: () => {
        toast.error('Failed to promote contact')
        setSaving(false)
      },
      onFinish: () => setSaving(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Convert Unknown Contact</DialogTitle>
          <DialogDescription>
            Attach this sender to a client record.
          </DialogDescription>
        </DialogHeader>

        {contact && (
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <p className="text-[13px] font-medium text-foreground">{contact.name || contact.email}</p>
            <p className="text-xs text-muted-foreground">{contact.email}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setMode('create')}
            className={`flex h-9 items-center justify-center gap-1.5 rounded-md border text-xs font-medium cursor-pointer ${
              mode === 'create' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-border text-muted-foreground hover:bg-muted'
            }`}
          >
            <UserPlus className="h-3.5 w-3.5" />
            New Client
          </button>
          <button
            type="button"
            onClick={() => setMode('existing')}
            className={`flex h-9 items-center justify-center gap-1.5 rounded-md border text-xs font-medium cursor-pointer ${
              mode === 'existing' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-border text-muted-foreground hover:bg-muted'
            }`}
          >
            <Building2 className="h-3.5 w-3.5" />
            Existing
          </button>
        </div>

        {mode === 'create' ? (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Client Name</label>
            <Input
              value={clientName}
              onChange={(event) => setClientName(event.target.value)}
              placeholder="Company name"
              className="h-9 text-[13px]"
            />
          </div>
        ) : (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Client</label>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="h-9 w-full cursor-pointer">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {emailThreadId && clientlessShipments.length > 0 && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Backfill Shipment</label>
            <Select value={selectedShipmentId || 'none'} onValueChange={(value) => setSelectedShipmentId(value === 'none' ? '' : value)}>
              <SelectTrigger className="h-9 w-full cursor-pointer">
                <SelectValue placeholder="Select shipment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Do not backfill shipment</SelectItem>
                {clientlessShipments.map((shipment) => (
                  <SelectItem key={shipment.id} value={shipment.id}>
                    {(shipment.origin_port || '--') + ' to ' + (shipment.destination_port || '--')}
                    {shipment.unassigned_bookings_count ? ` (${shipment.unassigned_bookings_count} unassigned bookings)` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="cursor-pointer" onClick={onClose}>
            Cancel
          </Button>
          <Button className="cursor-pointer" onClick={submit} disabled={!canSubmit || saving}>
            {saving ? 'Saving...' : 'Convert'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
